import { documentDisplayRef } from '../../modules/docs/utils/invoiceLedger';

export type ActivityTone = 'primary' | 'success' | 'neutral' | 'warning' | 'danger';

export type ActivityItem = {
  id: string;
  label: string;
  at: string;
  tone: ActivityTone;
  actorLabel?: string | null;
  /** The label already starts with the actor's first name. */
  actorInLabel?: boolean;
  /** Extra facts saved with the entry, for the full audit trail. */
  details?: string[];
};

export type ActivityEventLogRow = {
  id: string;
  occurredAt: string;
  action: string;
  label: string;
  actorId?: string | null;
  actorLabel?: string | null;
  metadata?: Record<string, unknown> | null;
};

export type ActivityDocument = {
  id: string;
  kind: string;
  lifecycleRef?: string | null;
  invoiceRef?: string | null;
  status?: string | null;
  createdAt?: string | null;
  statusChangedAt?: string | null;
  sentAt?: string | null;
  acceptedAt?: string | null;
  rejectedAt?: string | null;
  invoicedAt?: string | null;
  paidAt?: string | null;
  voidedAt?: string | null;
  /** FreeAgent status updates that succeeded for this invoice (mark_sent, mark_paid, mark_cancelled). */
  freeAgentSteps?: string[] | null;
};

export type ActivitySurvey = {
  id: string;
  kind: string;
  label?: string | null;
  completionStatus?: string | null;
  createdAt?: string | null;
  completedAt?: string | null;
  reopenedAt?: string | null;
  completedByLabel?: string | null;
  reopenedByLabel?: string | null;
};

export type ActivityProjectEvent = {
  id: string;
  eventType?: string | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

export type ActivityNotification = {
  id: string;
  type?: string | null;
  message?: string | null;
  createdAt?: string | null;
};

export type ActivityJobCompletion = {
  id: string;
  action?: string | null;
  attestedAt?: string | null;
  actorLabel?: string | null;
};

export type ActivityEmail = {
  id: string;
  templateType?: string | null;
  status?: string | null;
  subject?: string | null;
  createdAt?: string | null;
  metadata?: Record<string, unknown> | null;
};

function stamp(value?: string | null): string {
  return String(value || '').trim();
}

function ms(value?: string | null): number {
  const n = Date.parse(String(value || ''));
  return Number.isFinite(n) ? n : 0;
}

function docNoun(kind?: string | null): string {
  const k = String(kind || '').toLowerCase();
  if (k === 'invoice') return 'Invoice';
  if (k === 'estimate') return 'Estimate';
  if (k === 'quotation') return 'Quote';
  return 'Document';
}

export const FINAL_SURVEY_CREATED_BY_SYSTEM = 'Final survey created by system';

function isFinalCreatedBySystemEvent(row: ActivityEventLogRow): boolean {
  if (String(row.action || '') !== 'survey.final_created') return false;
  return !/failed/i.test(String(row.label || ''));
}

function displayActivityEventLabel(row: ActivityEventLogRow): string {
  const label = String(row.label || '').trim();
  if (isFinalCreatedBySystemEvent(row)) return FINAL_SURVEY_CREATED_BY_SYSTEM;
  return label;
}

function withRef(label: string, lifecycleRef?: string | null): string {
  const ref = String(lifecycleRef || '').trim();
  return ref ? `${label} (${ref})` : label;
}

export function calendarEventLabel(eventType: string): string | null {
  switch (String(eventType || '').trim()) {
    case 'firstSurveyAppointmentConfirmation':
      return 'First survey booked';
    case 'finalSurveyAppointmentConfirmation':
      return 'Final survey booked';
    case 'installationDateConfirmation':
      return 'Installation booked';
    case 'deliveryDateConfirmation':
      return 'Delivery booked';
    case 'firstSurveyAppointmentRequest':
      return 'First survey request sent';
    case 'finalSurveyAppointmentRequest':
      return 'Final survey request sent';
    default:
      return null;
  }
}

export function notificationActivityLabel(type: string, message?: string | null): string | null {
  const t = String(type || '').trim();
  if (t === 'doc_opened') return 'Customer opened quote';
  if (t === 'doc_accepted') return 'Customer accepted quote';
  if (t === 'doc_rejected') return 'Customer rejected quote';
  if (t === 'doc_query') return 'Customer query received';
  if (t === 'quote_accepted_final_survey') return 'Final Survey ready';
  if (t === 'job_comment') return 'Job comment added';
  if (t === 'job_completed') return 'Job marked complete';
  if (t === 'job_reopened') return 'Job reopened';
  if (t === 'lead_created') return null;
  const msg = String(message || '').trim();
  if (msg) return msg.length > 80 ? `${msg.slice(0, 77)}…` : msg;
  return null;
}

function push(
  items: ActivityItem[],
  id: string,
  label: string,
  at: string | null | undefined,
  tone: ActivityTone,
  actorLabel?: string | null,
  details?: string[],
) {
  const when = stamp(at);
  if (!when || !label) return;
  items.push({ id, label, at: when, tone, actorLabel: actorLabel || null, ...(details?.length ? { details } : {}) });
}

function freeAgentDetail(doc: ActivityDocument, step: string, text: string): string[] | undefined {
  return doc.freeAgentSteps?.includes(step) ? [text] : undefined;
}

/** "Simon Smith" -> "Simon"; "Admin" stays "Admin". */
export function actorFirstName(name?: string | null): string | null {
  const text = String(name || '').trim();
  if (!text) return null;
  const local = text.includes('@') && !text.includes(' ') ? text.split('@')[0] : text;
  return local.split(/\s+/)[0] || null;
}

const ACTIVITY_VERBS = [
  'status set to', 'rolled back', 'emailed', 'created', 'sent', 'marked', 'linked', 'deleted', 'reopened',
  'released', 'cleared', 'transmitted', 'changed', 'added', 'copied', 'booked', 'accepted', 'rejected',
  'voided', 'invoiced', 'reviewed', 'issued', 'updated', 'ended',
];
const ACTIVITY_VERB_PATTERN = new RegExp(`^(.+?) (${ACTIVITY_VERBS.join('|')})\\b(.*)$`);

/**
 * "Final Survey marked complete" by Simon -> "Simon marked Final Survey complete".
 * Returns null when the label does not read as something the person did.
 */
export function activitySentence(label: string, actorLabel?: string | null): string | null {
  const actor = actorFirstName(actorLabel);
  if (!actor) return null;
  if (label === FINAL_SURVEY_CREATED_BY_SYSTEM || /^Customer (accepted|rejected|opened|query)/.test(label)) return null;
  const match = label.match(ACTIVITY_VERB_PATTERN);
  if (!match) return null;
  const [, subject, verb, rest] = match;
  const thing = /^(First|Final) Survey/.test(subject) ? subject : subject.charAt(0).toLowerCase() + subject.slice(1);
  if (verb === 'status set to') return `${actor} set ${thing} status to${rest}`;
  if (verb === 'marked') return `${actor} marked ${thing}${rest}`;
  return `${actor} ${verb} ${thing}${rest}`;
}

function withActorSentence(item: ActivityItem): ActivityItem {
  const sentence = activitySentence(item.label, item.actorLabel);
  return sentence ? { ...item, label: sentence, actorInLabel: true } : item;
}

function money(value: unknown): string | null {
  const n = Number(value);
  if (value == null || !Number.isFinite(n)) return null;
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n);
}

function titleCase(value: unknown): string {
  const text = String(value || '').trim().replace(/_/g, ' ');
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : '';
}

const SURVEY_CLASSIFICATIONS: Record<string, string> = {
  pending_review: 'Changes waiting for reapproval review',
  no_reapproval: 'No customer reapproval needed',
  customer_reapproval_required: 'Customer reapproval required',
};

/** Human-readable facts from an event's saved details. Internal ids are left out. */
export function activityEventDetails(row: Pick<ActivityEventLogRow, 'action' | 'metadata'>): string[] {
  const meta = row.metadata || {};
  const details: string[] = [];
  const add = (text: string | null | false | undefined) => { if (text) details.push(text); };
  switch (row.action) {
    case 'document.status_changed':
      add(meta.from && meta.to ? `Status: ${titleCase(meta.from)} → ${titleCase(meta.to)}` : null);
      break;
    case 'payment_schedule.created_from_quote':
    case 'payment_schedule.created_retrospective': {
      add(money(meta.agreedValueAtLastCalc) && `Agreed value ${money(meta.agreedValueAtLastCalc)}`);
      const stages = Array.isArray(meta.stages) ? meta.stages as Array<Record<string, unknown>> : [];
      add(stages.length > 0 && stages
        .map((stage) => `${String(stage.label || titleCase(stage.kind))} ${money(stage.plannedAmount) || ''}`.trim())
        .join(' · '));
      break;
    }
    case 'payment_schedule.review_required':
      add(money(meta.previousAgreedValue) && money(meta.currentAgreedValue)
        && `Agreed value ${money(meta.previousAgreedValue)} → ${money(meta.currentAgreedValue)}`);
      add(money(meta.remainingToInvoice) && `${money(meta.remainingToInvoice)} left to invoice`);
      break;
    case 'payment_schedule.stage_issued':
      add(Number(meta.issuedAmount) > 0 && `Issued ${money(meta.issuedAmount)}`);
      break;
    case 'payment_schedule.stage_rolled_back':
      add(meta.reason === 'invoice_deleted' ? 'Because the invoice was deleted' : null);
      break;
    case 'payment_schedule.removed':
      add(money(meta.invoicedToDate) && `${money(meta.invoicedToDate)} already invoiced, invoices kept`);
      break;
    case 'supplier.reference_added':
      add(meta.reference ? `Reference ${String(meta.reference)}` : null);
      break;
    case 'project.customer_changed':
      add(meta.customerName ? `New customer: ${String(meta.customerName)}` : null);
      break;
    case 'survey.final_completed':
    case 'survey.final_operational':
    case 'survey.final_reapproval_required':
    case 'survey.final_live_updated_without_reapproval':
      add(SURVEY_CLASSIFICATIONS[String(meta.classification || '')] || null);
      add(meta.comparedAgainst === 'first_survey' ? 'Compared against the First Survey' : null);
      break;
    case 'survey.cleared':
      add(meta.reprovisioned ? 'A fresh survey was started in its place' : null);
      break;
    default:
      break;
  }
  return details;
}

export function toneForActivityAction(action: string): ActivityTone {
  const a = String(action || '').toLowerCase();
  if (a.includes('delete') || a.includes('reject') || a.includes('void') || a.includes('cancel')) return 'danger';
  if (a.includes('release') || a.includes('reopen')) return 'warning';
  if (
    a.includes('complete')
    || a.includes('accept')
    || a.includes('paid')
    || a.includes('cash')
    || a.includes('final_created')
    || a.includes('sent')
    || a.includes('order')
  ) return 'success';
  if (a.includes('created') || a.includes('assigned') || a.includes('linked')) return 'primary';
  return 'neutral';
}

/** Logged while the page was still loading the quote, so the total read as £0. */
export function isFalseScheduleReviewEvent(row: Pick<ActivityEventLogRow, 'action' | 'metadata'>): boolean {
  if (row.action !== 'payment_schedule.review_required') return false;
  const current = row.metadata?.currentAgreedValue;
  return current != null && Number(current) === 0;
}

export function activityEventLogToItems(rows: ActivityEventLogRow[]): ActivityItem[] {
  const items: ActivityItem[] = [];
  for (const row of rows || []) {
    if (isFalseScheduleReviewEvent(row)) continue;
    const at = stamp(row.occurredAt);
    const label = displayActivityEventLabel(row);
    if (!at || !label) continue;
    items.push({
      id: `evt-${row.id}`,
      label,
      at,
      tone: toneForActivityAction(row.action),
      actorLabel: row.actorLabel ? String(row.actorLabel) : null,
      details: activityEventDetails(row),
    });
  }
  return items;
}

/**
 * Build a lifecycle-focused activity feed. Skips trivial field edits; prefers
 * document/survey creation, status milestones, calendar bookings, and job events.
 * Pass `eventLog` to surface append-only jarvis_activity_events (merged + deduped).
 */
export function buildWorkspaceActivityItems(input: {
  project: {
    id: string;
    created_at?: string | null;
    customer_id?: string | null;
    customer_name?: string | null;
    status_changed_at?: string | null;
    completed_at?: string | null;
    cancelled_at?: string | null;
    project_status?: string | null;
    status?: string | null;
    onedrive_folder_id?: string | null;
  };
  projectStatusLabel: string;
  documents?: ActivityDocument[];
  surveys?: ActivitySurvey[];
  events?: ActivityProjectEvent[];
  notifications?: ActivityNotification[];
  jobCompletions?: ActivityJobCompletion[];
  emails?: ActivityEmail[];
  eventLog?: ActivityEventLogRow[];
  limit?: number;
}): ActivityItem[] {
  const next: ActivityItem[] = [];
  const project = input.project;

  push(next, 'project-created', 'Project created', project.created_at, 'primary');
  if (project.customer_id || project.customer_name) {
    push(next, 'customer-linked', 'Customer linked', project.created_at, 'primary');
  }
  push(
    next,
    'project-status',
    `Project status set to ${input.projectStatusLabel}`,
    project.status_changed_at || project.completed_at || project.cancelled_at || project.created_at,
    input.projectStatusLabel === 'Cancelled' ? 'danger' : 'success',
  );
  if (project.onedrive_folder_id) {
    // Folder link has no dedicated timestamp; use project update-ish fallback only if distinct.
    // Skip unless we later store linked_at — avoid fake chronology.
  }

  for (const doc of input.documents || []) {
    const noun = docNoun(doc.kind);
    const status = String(doc.status || '').toLowerCase();
    const displayRef = documentDisplayRef(doc);
    push(next, `doc-created-${doc.id}`, withRef(`${noun} created`, displayRef), doc.createdAt, 'primary');

    if (doc.sentAt) {
      push(
        next, `doc-sent-${doc.id}`, withRef(`${noun} sent`, displayRef), doc.sentAt, 'neutral', null,
        freeAgentDetail(doc, 'mark_sent', 'Marked sent in FreeAgent'),
      );
    }
    if (doc.acceptedAt) {
      push(next, `doc-accepted-${doc.id}`, withRef(`${noun} accepted`, displayRef), doc.acceptedAt, 'success');
    }
    if (doc.rejectedAt) {
      push(
        next, `doc-rejected-${doc.id}`, withRef(`${noun} rejected`, displayRef), doc.rejectedAt, 'danger', null,
        freeAgentDetail(doc, 'mark_cancelled', 'Cancelled in FreeAgent'),
      );
    }
    if (doc.invoicedAt) {
      push(next, `doc-invoiced-${doc.id}`, withRef(`${noun} invoiced`, displayRef), doc.invoicedAt, 'success');
    }
    if (doc.paidAt) {
      const paidLabel = status === 'cash'
        ? withRef(`${noun} marked Cash`, displayRef)
        : withRef(`${noun} marked Paid`, displayRef);
      push(
        next, `doc-paid-${doc.id}`, paidLabel, doc.paidAt, 'success', null,
        freeAgentDetail(doc, 'mark_paid', 'Marked paid in FreeAgent'),
      );
    }
    if (doc.voidedAt) {
      push(next, `doc-voided-${doc.id}`, withRef(`${noun} voided`, displayRef), doc.voidedAt, 'warning');
    }
    // Status fallback when only status_changed_at exists (e.g. draft rollback)
    if (
      doc.statusChangedAt
      && status
      && status !== 'draft'
      && !doc.sentAt
      && !doc.acceptedAt
      && !doc.rejectedAt
      && !doc.paidAt
      && !doc.invoicedAt
      && !doc.voidedAt
    ) {
      const label = status.charAt(0).toUpperCase() + status.slice(1);
      push(
        next,
        `doc-status-${doc.id}-${status}`,
        withRef(`${noun} status set to ${label}`, displayRef),
        doc.statusChangedAt,
        'neutral',
      );
    }
  }

  const hasFinalCreatedEvent = (input.eventLog || []).some(isFinalCreatedBySystemEvent);

  for (const survey of input.surveys || []) {
    const kind = String(survey.kind || '').toLowerCase();
    if (kind === 'first') {
      push(next, `survey-created-${survey.id}`, 'First Survey created', survey.createdAt, 'primary');
    } else if (kind === 'final') {
      if (!hasFinalCreatedEvent) {
        push(next, `survey-created-${survey.id}`, FINAL_SURVEY_CREATED_BY_SYSTEM, survey.createdAt, 'success');
      }
      if (survey.completedAt) {
        push(next, `survey-complete-${survey.id}`, 'Final Survey marked complete', survey.completedAt, 'success', survey.completedByLabel);
      }
      if (survey.reopenedAt) {
        push(next, `survey-reopened-${survey.id}`, 'Final Survey reopened', survey.reopenedAt, 'warning', survey.reopenedByLabel);
      }
    } else if (kind === 'intermediate' && survey.label) {
      push(next, `survey-created-${survey.id}`, `${survey.label} created`, survey.createdAt, 'neutral');
    }
  }

  let latestCalendarUpdate: ActivityItem | null = null;
  for (const row of input.events || []) {
    const label = calendarEventLabel(String(row.eventType || ''));
    const created = stamp(row.createdAt);
    const updated = stamp(row.updatedAt);
    if (label && created) {
      push(next, `event-created-${row.id}`, label, created, 'success');
    }
    if (updated && updated !== created) {
      const item: ActivityItem = {
        id: `event-updated-${row.id}`,
        label: 'Calendar event updated',
        at: updated,
        tone: 'neutral',
      };
      if (!latestCalendarUpdate || ms(item.at) > ms(latestCalendarUpdate.at)) {
        latestCalendarUpdate = item;
      }
    }
  }
  if (latestCalendarUpdate) next.push(latestCalendarUpdate);

  for (const note of input.notifications || []) {
    const label = notificationActivityLabel(String(note.type || ''), note.message);
    if (!label) continue;
    push(next, `notif-${note.id}`, label, note.createdAt, note.type === 'doc_rejected' ? 'danger' : 'success');
  }

  for (const job of input.jobCompletions || []) {
    const action = String(job.action || '').toLowerCase();
    if (action === 'released') {
      push(next, `job-released-${job.id}`, 'Job released', job.attestedAt, 'warning', job.actorLabel);
    } else if (action === 'ended') {
      push(next, `job-ended-${job.id}`, 'Job card ended', job.attestedAt, 'neutral', job.actorLabel);
    } else {
      push(next, `job-completed-${job.id}`, 'Job marked complete', job.attestedAt, 'success', job.actorLabel);
    }
  }

  for (const email of input.emails || []) {
    const template = String(email.templateType || '').toLowerCase();
    const status = String(email.status || '').toLowerCase();
    const created = stamp(email.createdAt);
    if (!created || (status !== 'sent' && status !== 'draft')) continue;
    const meta = email.metadata && typeof email.metadata === 'object' ? email.metadata : {};
    const kind = String((meta as { kind?: string }).kind || '');
    if (kind === 'visual_designer_supplier_transmission' || template === 'windoworder') {
      push(
        next,
        `email-supplier-${email.id}`,
        status === 'draft' ? 'Supplier order draft created' : 'Supplier order sent',
        created,
        'success',
      );
      continue;
    }
    if (
      template.includes('send') && (template.includes('estimate') || template.includes('quote') || template.includes('invoice'))
    ) {
      const noun = template.includes('invoice') ? 'Invoice' : template.includes('estimate') ? 'Estimate' : 'Quote';
      push(
        next,
        `email-doc-${email.id}`,
        status === 'draft' ? `${noun} draft email created` : `${noun} emailed to customer`,
        created,
        'neutral',
      );
    }
  }

  next.push(...activityEventLogToItems(input.eventLog || []));

  next.sort((a, b) => ms(b.at) - ms(a.at));
  const byKey = new Map<string, ActivityItem>();
  for (const item of next) {
    const key = `${item.label}|${item.at}`;
    const prev = byKey.get(key);
    if (!prev) {
      byKey.set(key, item);
      continue;
    }
    // Prefer append-only event-log rows (they carry actor attribution).
    if (item.id.startsWith('evt-') && !prev.id.startsWith('evt-')) {
      byKey.set(key, item);
    }
  }
  return Array.from(byKey.values())
    .sort((a, b) => ms(b.at) - ms(a.at))
    .slice(0, Math.max(1, input.limit ?? 10))
    .map(withActorSentence);
}
